import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { NodeStatus } from "../components/NodeStatus";
import { ReceiptCard } from "../components/ReceiptCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClubCiergeLab() {
  const [fixture, setFixture] = useState(null);
  const [actor, setActor] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [nodeStatus, setNodeStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/labs/clubcierge/fixture`).then((r) => setFixture(r.data));
    axios.get(`${API}/katlas/status`).then((r) => setNodeStatus(r.data)).catch(() => {});
  }, []);

  const submit = async (decision) => {
    if (!actor.trim()) {
      setError({ message: "Please enter the Club Secretary's display name to act under authority." });
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/labs/clubcierge/approve`, {
        decision,
        actor_name: actor.trim(),
        note: note.trim() || null,
      });
      setReceipt(r.data.receipt);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      setError({
        code: detail.error || "request_failed",
        message:
          detail.message ||
          "The request failed. No simulated receipt was produced.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const nodeBlocked =
    nodeStatus &&
    nodeStatus.status !== "active" &&
    !nodeStatus.simulation_fallback_allowed;

  return (
    <div className="min-h-screen bg-[#020B14] text-white" id="top">
      <Nav />
      <main className="pt-24 pb-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          {/* Breadcrumb + status */}
          <div className="flex flex-wrap items-center gap-4 justify-between mb-10">
            <div className="flex items-center gap-3 mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <Link to="/" data-testid="crumb-home" className="hover:text-white">Platform</Link>
              <span>/</span>
              <Link to="/#cierge-labs" data-testid="crumb-labs" className="hover:text-white">Cierge Labs</Link>
              <span>/</span>
              <span className="text-slate-200">ClubCierge</span>
            </div>
            <NodeStatus />
          </div>

          {/* Title */}
          <header className="max-w-3xl">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              ClubCierge · Member sponsor introduction
            </div>
            <h1
              data-testid="lab-headline"
              className="font-display text-4xl sm:text-5xl mt-4 leading-[1.05]"
            >
              One governed action.
              <br />
              <span className="text-slate-400">One KATLAS-signed receipt.</span>
            </h1>
            <p className="mt-6 text-slate-400 text-sm leading-relaxed">
              A junior member's parent-held profile is in custody. The Club
              Secretary holds authority over sponsor introductions. KATLAS
              records who acted, under what authority, what evidence was
              shared and what was withheld — without exposing the underlying
              private data.
            </p>
            <p className="mt-3 text-xs text-slate-500 italic max-w-2xl">
              AI may draft the introduction summary. AI does not approve, and
              it does not access withheld profile fields.
            </p>
          </header>

          {/* Configuration warning */}
          {nodeBlocked && (
            <div
              data-testid="node-blocked-banner"
              className="mt-10 border border-rose-500/30 bg-rose-950/30 p-5 mono text-xs text-rose-200 leading-relaxed"
            >
              <div className="uppercase tracking-[0.2em] mb-2 text-rose-300">
                Live signing unavailable · {nodeStatus.label}
              </div>
              <div className="font-sans text-sm text-rose-100/90">
                {nodeStatus.status === "misconfigured"
                  ? "The KATLAS bearer credential is missing. A legacy credential is present in the environment but is intentionally ignored — bearer authentication requires the canonical bearer-token variable defined in the KATLAS deployment runbook. The legacy variable is reserved for a future API-key mechanism and is never accepted for bearer auth."
                  : "This lab will not produce a receipt while the KATLAS node is unreachable or unconfigured. Simulation fallback is disabled for this build, so no fake receipt will be returned."}
              </div>
            </div>
          )}

          {!fixture && (
            <div className="mt-12 mono text-xs uppercase tracking-[0.2em] text-slate-500">
              Loading lab fixture…
            </div>
          )}

          {fixture && (
            <div className="mt-12 grid lg:grid-cols-12 gap-px bg-white/[0.06] border border-white/[0.06]">
              {/* CAR pillars */}
              <div className="bg-[#020B14] p-6 lg:p-7 lg:col-span-4">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]">
                  Custody
                </div>
                <h3 className="font-display text-lg mt-2">Who controls the evidence</h3>
                <dl className="mt-4 space-y-2 mono text-xs text-slate-200">
                  <Pair k="subject" v={fixture.custody.subject_ref} />
                  <Pair k="role" v={fixture.custody.subject_role} />
                  <Pair k="held by" v={fixture.custody.wallet_held_by} />
                </dl>
                <p className="mt-4 text-xs text-slate-500 italic">
                  {fixture.custody.description}
                </p>
              </div>

              <div className="bg-[#020B14] p-6 lg:p-7 lg:col-span-4">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]">
                  Authority
                </div>
                <h3 className="font-display text-lg mt-2">Who governs the action</h3>
                <dl className="mt-4 space-y-2 mono text-xs text-slate-200">
                  <Pair k="actor role" v={fixture.authority.actor_role} />
                  <Pair k="policy" v={fixture.authority.policy_ref} />
                  <Pair k="gate" v={fixture.authority.gate} />
                </dl>
              </div>

              <div className="bg-[#020B14] p-6 lg:p-7 lg:col-span-4">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">
                  Request
                </div>
                <h3 className="font-display text-lg mt-2">What is being asked</h3>
                <dl className="mt-4 space-y-2 mono text-xs text-slate-200">
                  <Pair k="type" v={fixture.request.type} />
                  <Pair k="to partner" v={fixture.request.to_partner_ref} />
                  <Pair k="ai assistance" v={fixture.request.ai_assistance} />
                </dl>
                <p className="mt-4 text-xs text-slate-500 italic">
                  {fixture.request.purpose}
                </p>
              </div>
            </div>
          )}

          {/* Evidence visibility matrix */}
          {fixture && (
            <section className="mt-12">
              <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]">
                    Evidence visibility matrix
                  </div>
                  <h2 className="font-display text-2xl mt-2">
                    Who sees what — and what stays in custody
                  </h2>
                </div>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 max-w-md">
                  KATLAS receives refs, hashes and counts — never raw private data.
                </p>
              </div>

              <div
                data-testid="evidence-matrix"
                className="border border-white/[0.08] overflow-x-auto"
              >
                <table className="w-full text-xs mono">
                  <thead>
                    <tr className="bg-white/[0.04] text-slate-500 uppercase tracking-[0.16em]">
                      <th className="text-left p-3">Evidence ref</th>
                      <th className="text-left p-3">Kind</th>
                      <th className="text-left p-3">Controlled by</th>
                      <th className="text-center p-3">Requester</th>
                      <th className="text-center p-3">Approver</th>
                      <th className="text-center p-3">→ KATLAS node</th>
                      <th className="text-center p-3">Public share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixture.evidence_matrix.map((row) => (
                      <tr
                        key={row.ref}
                        className="border-t border-white/[0.06] text-slate-200"
                      >
                        <td className="p-3 text-slate-100">{row.ref}</td>
                        <td className="p-3 text-slate-400">{row.kind}</td>
                        <td className="p-3 text-slate-400">{row.controlled_by}</td>
                        <td className="p-3 text-center"><Tick on={row.visible_to_requester} /></td>
                        <td className="p-3 text-center"><Tick on={row.visible_to_approver} /></td>
                        <td className="p-3 text-center"><Tick on={row.sent_to_katlas_node} accent /></td>
                        <td className="p-3 text-center"><Tick on={row.public_share} accent /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-slate-500 italic max-w-3xl">
                {fixture.data_boundary_statement}
              </p>
            </section>
          )}

          {/* Action */}
          {fixture && !receipt && (
            <section className="mt-14">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]">
                Governed action
              </div>
              <h2 className="font-display text-2xl mt-2">
                Act under authority and sign a receipt
              </h2>

              <div className="mt-6 grid lg:grid-cols-12 gap-px bg-white/[0.06] border border-white/[0.06]">
                <div className="bg-[#020B14] p-6 lg:col-span-7 space-y-5">
                  <label className="block">
                    <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Acting as · Club Secretary (display name)
                    </span>
                    <input
                      data-testid="actor-input"
                      value={actor}
                      onChange={(e) => setActor(e.target.value)}
                      placeholder="e.g. J. Patel"
                      className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#0066FF] outline-none p-3 text-sm text-white"
                      maxLength={64}
                    />
                  </label>
                  <label className="block">
                    <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Note (optional · presence only is recorded on the receipt)
                    </span>
                    <textarea
                      data-testid="note-input"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Internal note. The body is not sent to KATLAS."
                      rows={3}
                      maxLength={240}
                      className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#0066FF] outline-none p-3 text-sm text-white"
                    />
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      data-testid="approve-btn"
                      onClick={() => submit("approve")}
                      disabled={submitting || nodeBlocked}
                      className="cta-primary inline-flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 text-sm font-medium rounded-sm"
                    >
                      {submitting ? "Signing receipt…" : "Approve & sign"}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </button>
                    <button
                      data-testid="refuse-btn"
                      onClick={() => submit("refuse")}
                      disabled={submitting || nodeBlocked}
                      className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 text-sm font-medium rounded-sm"
                    >
                      Refuse
                    </button>
                  </div>

                  {error && (
                    <div
                      data-testid="error-banner"
                      className="border border-rose-500/30 bg-rose-950/30 p-4 mono text-xs text-rose-200"
                    >
                      <div className="uppercase tracking-[0.18em] text-rose-300 mb-1">
                        {error.code || "error"}
                      </div>
                      <div className="font-sans text-sm text-rose-100">{error.message}</div>
                    </div>
                  )}
                </div>

                <aside className="bg-[#020B14] p-6 lg:col-span-5 border-t lg:border-t-0 lg:border-l border-white/[0.06]">
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Operating principle
                  </div>
                  <p className="mt-3 text-sm text-white leading-relaxed">
                    AI suggests. Wallets prove. Authorised roles decide.{" "}
                    <span className="text-[#EAB308]">KATLAS signs the receipt.</span>
                  </p>
                  <div className="mt-6 mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    What will be signed
                  </div>
                  <ul className="mt-3 text-xs text-slate-300 space-y-1.5 mono">
                    <li>action: <span className="text-slate-100">{fixture.action}</span></li>
                    <li>custody: <span className="text-slate-100">{fixture.custody.subject_ref}</span></li>
                    <li>authority: <span className="text-slate-100">{fixture.authority.actor_role}</span></li>
                    <li>shared refs: <span className="text-slate-100">{fixture.evidence_matrix.filter((r) => r.sent_to_katlas_node).length}</span></li>
                    <li>withheld: <span className="text-slate-100">{fixture.evidence_matrix.filter((r) => !r.sent_to_katlas_node).length}</span></li>
                  </ul>
                </aside>
              </div>
            </section>
          )}

          {/* Receipt */}
          {receipt && (
            <section className="mt-14">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">
                    KATLAS-signed receipt
                  </div>
                  <h2 className="font-display text-2xl mt-2">
                    {receipt.decision === "approve" ? "Approved" : "Refused"} · recorded
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid="reset-btn"
                    onClick={() => { setReceipt(null); setActor(""); setNote(""); }}
                    className="border border-white/15 hover:border-white/40 px-4 py-2 mono text-[11px] uppercase tracking-[0.18em] text-white"
                  >
                    Run again
                  </button>
                  <button
                    data-testid="open-public-btn"
                    onClick={() => navigate(`/r/${encodeURIComponent(receipt.hash)}`)}
                    className="cta-primary inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-4 py-2 mono text-[11px] uppercase tracking-[0.18em]"
                  >
                    Open public-safe receipt
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
              <ReceiptCard receipt={receipt} />
            </section>
          )}

          {/* Footer link to live reference */}
          <section className="mt-20 border-t border-white/[0.06] pt-10">
            <div className="grid sm:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06]">
              <div className="bg-[#020B14] p-6">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]">
                  Validated live reference
                </div>
                <h3 className="font-display text-lg mt-2 text-white">
                  See the full ClubCierge deployment
                </h3>
                <p className="mt-2 text-xs text-slate-400">
                  This lab proves one governed action. The live ClubCierge
                  application runs the full member journey.
                </p>
                <a
                  href="https://clubcierge.katlastechnology.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lab-live-link"
                  className="mt-4 inline-flex items-center gap-2 border border-[#0066FF]/40 hover:border-[#0066FF] text-white px-4 py-2 mono text-[11px] uppercase tracking-[0.18em]"
                >
                  Visit live ClubCierge
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M9 7h8v8"/></svg>
                </a>
              </div>
              <div className="bg-[#020B14] p-6">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Status labels
                </div>
                <ul className="mt-3 mono text-xs space-y-1.5">
                  <li className="text-[#0066FF]">Live KATLAS node — connected, emits signed receipts</li>
                  <li className="text-slate-300">Simulation — bounded scenario, marked clearly</li>
                  <li className="text-slate-300">Concept — described, not deployed</li>
                  <li className="text-slate-500">Archived — retained for audit</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Pair = ({ k, v }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-slate-500 min-w-[80px]">{k}</span>
    <span className="text-slate-100 break-all">{v}</span>
  </div>
);

const Tick = ({ on, accent }) => {
  if (!on) return <span className="text-slate-600">·</span>;
  return (
    <span
      className={`inline-block w-2 h-2 ${accent ? "bg-[#EAB308]" : "bg-[#0066FF]"}`}
    />
  );
};
